'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { DehydratedState, QueryKey } from '@tanstack/react-query';
import { useIsHydrating } from '@/hooks/use-server-rendered-mount';
import { convexQueryHash } from '@/lib/convex-query-dehydrate';

const QueryHydrationSnapshotContext = createContext<DehydratedState | null>(
  null
);

/** The identity-checked data used to render this workspace's server HTML. */
export function QueryHydrationSnapshotProvider({
  state,
  children,
}: {
  state: DehydratedState | null;
  children: ReactNode;
}) {
  return (
    <QueryHydrationSnapshotContext.Provider value={state}>
      {children}
    </QueryHydrationSnapshotContext.Provider>
  );
}

/**
 * TanStack correctly preserves newer cache results when hydrating, but those
 * results can differ from the server HTML. Use the immutable snapshot only
 * for hydration, then render live data without writing over the live cache.
 */
export function useQueryHydrationData<T>(
  queryKey: QueryKey,
  liveData: T | undefined
): T | undefined {
  const snapshot = useContext(QueryHydrationSnapshotContext);
  const hydrating = useIsHydrating();
  if (!hydrating || !snapshot) return liveData;
  const hash = convexQueryHash(queryKey);
  // The query key owns the result type; dehydrated state erases that type.
  return snapshot.queries.find((query) => query.queryHash === hash)?.state
    .data as T | undefined;
}
